import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Terminal, Clock, Info, Cpu, Brain, ChevronRight, Search,
  FileText, Table, Globe, Code, HelpCircle, CheckCircle2, Share2, Zap, Users, Bot, Wrench
} from 'lucide-react';
import ApiClient from '../utils/api';
import EmptyAnalyticsState from '../components/EmptyAnalyticsState';


function TraceTreeNode({ node, index, expandedNodes, toggleNode, fmtLatency, prettyJson }) {
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expandedNodes.has(node.node_id);

  const renderContent = (content) => {
    if (!content) return null;
    if (typeof content === 'string') {
      try {
        const parsed = JSON.parse(content);
        return <pre className="text-[10px] bg-zinc-50 p-3 rounded-lg whitespace-pre-wrap break-words font-mono text-zinc-600 leading-relaxed">{JSON.stringify(parsed, null, 2)}</pre>;
      } catch (e) {
        return <p className="text-[11px] text-zinc-600 leading-relaxed break-words">{content}</p>;
      }
    }
    return <pre className="text-[10px] bg-zinc-50 p-3 rounded-lg whitespace-pre-wrap break-words font-mono text-zinc-600 leading-relaxed">{JSON.stringify(content, null, 2)}</pre>;
  };

  const getDisplayContent = (type) => {
    if (node.input_parsed) return node.input_parsed;
    const attrs = node.attributes || {};
    const meta = node.metadata || {};
    // Check common LLM/Tool/Agent keys
    const val = attrs.input_content || attrs.prompt || attrs.input || attrs.tool_input || attrs.arguments || attrs.value || meta.input;
    if (val) return val;
    // Fallback: search for any key containing 'input' or 'prompt'
    const fallbackKey = Object.keys(attrs).find(k => k.toLowerCase().includes('input') || k.toLowerCase().includes('prompt'));
    return fallbackKey ? attrs[fallbackKey] : null;
  };

  const getOutputContent = (type) => {
    if (node.output_parsed) return node.output_parsed;
    const attrs = node.attributes || {};
    const meta = node.metadata || {};
    const val = attrs.output_content || attrs.response || attrs.output || attrs.tool_output || attrs.result || attrs.completion || meta.output;
    if (val) return val;
    // Fallback: search for any key containing 'output', 'response', or 'result'
    const fallbackKey = Object.keys(attrs).find(k => k.toLowerCase().includes('output') || k.toLowerCase().includes('response') || k.toLowerCase().includes('result') || k.toLowerCase().includes('completion'));
    return fallbackKey ? attrs[fallbackKey] : null;
  };
  const type = node.type?.toLowerCase();

  const getIcon = () => {
    const name = (node.name || '').toLowerCase();

    // 1. Specialized Agents
    if (name.includes('pdf_specialist')) return <FileText size={16} className="text-rose-500" />;
    if (name.includes('docx_specialist')) return <FileText size={16} className="text-blue-500" />;
    if (name.includes('csv_specialist')) return <Table size={16} className="text-emerald-500" />;
    if (name.includes('web_specialist')) return <Globe size={16} className="text-sky-500" />;
    if (name.includes('api_specialist')) return <Code size={16} className="text-purple-500" />;
    if (name.includes('faq_specialist')) return <HelpCircle size={16} className="text-amber-500" />;
    if (name.includes('validation_specialist')) return <CheckCircle2 size={16} className="text-emerald-600" />;

    // 2. Core Actions
    if (name.includes('delegate_task_to_member')) return <Share2 size={16} className="text-indigo-500" />;
    if (name.includes('openaichat.ainvoke_stream')) return <Zap size={16} className="text-amber-500" />;

    // 3. Lead Agent / Generic Runs
    if (name.includes('ai_support_team')) return <Users size={16} className="text-indigo-600" />;
    if (name.includes('.run')) return <Bot size={16} className="text-blue-600" />;

    // 4. Tools & Fallbacks
    if (type === 'tool' || name.includes('tool')) return <Wrench size={16} className="text-zinc-600" />;

    if (type === 'llm') return <Cpu size={16} className="text-blue-500" />;
    if (type === 'agent') return <Brain size={16} className="text-indigo-500" />;
    return <Terminal size={16} className="text-zinc-500" />;
  };

  return (
    <div className="space-y-2 animate-fadeIn">
      <div
        onClick={() => toggleNode(node.node_id)}
        className={`convo-trace-item px-4 py-2 rounded-2xl border transition-all cursor-pointer ${
          isExpanded ? 'border-blue-400 bg-blue-50/40 shadow-sm' : 'border-zinc-300 hover:border-blue-300'
          }`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-8 h-8 rounded-full bg-white border border-zinc-100 flex items-center justify-center shadow-sm overflow-hidden flex-shrink-0">
              {getIcon()}
            </div>
            <span className="text-[13px] font-bold text-zinc-700 tracking-tight">{node.name}</span>
          </div>
          <div className="flex items-center gap-4 text-[10px] font-bold text-zinc-400">
            <div className="flex items-center gap-1">
              <Clock size={10} />
              {fmtLatency(node.latency_ms)}
            </div>
            {hasChildren && (
              <div className={`transition-transform duration-300 ${isExpanded ? 'rotate-90 text-blue-500' : ''}`}>
                <ChevronRight size={14} />
              </div>
            )}
          </div>
        </div>

        {isExpanded && (
          <div className="mt-4 space-y-6 pt-4 border-t border-blue-100/50">
            <div className="flex flex-col gap-6">
              <div className="space-y-2">
                <div className="flex items-center gap-2 px-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                  <span className="text-[9px] font-black text-zinc-400 uppercase tracking-wider font-semibold">Input Payload</span>
                </div>
                {renderContent(getDisplayContent(type))}
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2 px-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                  <span className="text-[9px] font-black text-zinc-400 uppercase tracking-wider font-semibold">Output Response</span>
                </div>
                {renderContent(getOutputContent(type))}
              </div>
            </div>

            {node.metadata && Object.keys(node.metadata).length > 0 && (
              <div className="space-y-2">
                <span className="text-[9px] font-black text-zinc-400 uppercase tracking-wider font-semibold px-1">Node Metadata</span>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(node.metadata).map(([k, v]) => (
                    <div key={k} className="px-2 py-1 bg-zinc-100 rounded text-[9px] font-bold text-zinc-500">
                      <span className="text-zinc-400 mr-1">{k}:</span> {String(v)}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {isExpanded && hasChildren && (
        <div className="convo-subtree-inner relative ml-4 pl-6 space-y-2 mt-2">
          {node.children.map((child, i) => (
            <div key={child.node_id} className="relative">
              {/* L-shaped connector - extended top to join prev */}
              <div className="absolute -left-6 -top-3 bottom-1/2 w-6 border-l border-b border-zinc-200 rounded-bl-xl" />
              {/* Continued vertical line for siblings - extended bottom to join next */}
              {i < node.children.length - 1 && (
                <div className="absolute -left-6 top-1/2 -bottom-3 w-6 border-l border-zinc-200" />
              )}

              <TraceTreeNode
                node={child}
                index={i}
                expandedNodes={expandedNodes}
                toggleNode={toggleNode}
                fmtLatency={fmtLatency}
                prettyJson={prettyJson}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const getAvatarSeed = (userId, sessionId) => {
  const normalized = (userId || '').trim().toLowerCase();
  if (!normalized || ['guest', 'none', 'null', 'undefined', 'anonymous'].includes(normalized)) {
    return 'Anonymous';
  }
  return userId;
};



const renderMessageText = (text) => {
  if (!text) return null;

  if (typeof text !== 'string') {
    if (text && typeof text === 'object') {
      if (text.answer) return text.answer;
      return JSON.stringify(text);
    }
    return String(text);
  }
  
  let cleanText = text;
  cleanText = cleanText.replace(/::IMAGE::(\S+)/g, 'IMAGE_REF:$1');
  
  const parts = cleanText.split(/(IMAGE_REF:[^\s<"')]+)/g);
  return parts.map((part, index) => {
    if (part.startsWith('IMAGE_REF:')) {
      const path = part.substring(10);
      let cleanPath = path;
      let fullUrl = cleanPath;
      if (cleanPath.startsWith('/uploads')) {
        const apiBase = process.env.REACT_APP_API_BASE_URL || '';
        const baseDomain = apiBase.endsWith('/api') ? apiBase.slice(0, -4) : apiBase;
        fullUrl = `${baseDomain}${cleanPath}`;
      }
      return (
        <img
          key={index}
          src={fullUrl}
          alt="Visual Context"
          style={{
            maxWidth: '100%',
            maxHeight: '300px',
            borderRadius: '12px',
            marginTop: '8px',
            display: 'block',
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
          }}
        />
      );
    }
    return <span key={index}>{part}</span>;
  });
};

export default function AnalyticsConversations({ activeAiAgentId, contactSessionIds, isContactDetail = false }) {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [, setError] = useState(null);
  const [searchParams] = useSearchParams();
  const sessionIdParam = searchParams.get('session_id');
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionTraces, setSessionTraces] = useState({});
  const [expandedTraceNodes, setExpandedTraceNodes] = useState(new Set());
  const [convoShownTraceIds, setConvoShownTraceIds] = useState(new Set());
  const [showParams, setShowParams] = useState(false);
  const [convoPage, setConvoPage] = useState(1);
  const [hoveredRunId, setHoveredRunId] = useState(null);
  const [searchTerm, setSearchTerm] = useState(sessionIdParam || '');
  const [debouncedSearch, setDebouncedSearch] = useState(sessionIdParam || '');
  const itemsPerPage = 10;
  const formatTime = (epoch) => {
    if (!epoch) return '';
    const timestamp = epoch < 10000000000 ? epoch * 1000 : epoch;
    return new Date(timestamp).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Kolkata'
    });
  };

  const fetchData = useCallback(async () => {
    if (contactSessionIds && contactSessionIds.length === 0) {
      setData({ sessions: [], total_count: 0 });
      return;
    }
    setIsLoading(true);
    try {
      const sessionIdsParam = contactSessionIds ? `&session_ids=${encodeURIComponent(contactSessionIds.join(','))}` : '';
      const res = await ApiClient.request(
        `/analytics/dashboard/conversations?page=${convoPage}&page_size=${itemsPerPage}&search=${encodeURIComponent(debouncedSearch)}${sessionIdsParam}`,
        {
          headers: { 'X-AI-Agent-ID': activeAiAgentId }
        }
      );
      setData(res);
      setError(null);
    } catch (err) {
      console.error('[AnalyticsConversations] Fetch error:', err);
      setError(err.message || 'Failed to sync with conversation analytics');
    } finally {
      setIsLoading(false);
    }
  }, [activeAiAgentId, convoPage, debouncedSearch, contactSessionIds]);

  const fetchSessionTraces = useCallback(async (sessionId) => {
    try {
      const data = await ApiClient.request(`/analytics/traces/${sessionId}`, {
        headers: { 'X-AI-Agent-ID': activeAiAgentId }
      });
      setSessionTraces(prev => ({ ...prev, [sessionId]: data.traces || [] }));
    } catch (error) {
      console.error("Error fetching traces:", error);
    }
  }, [activeAiAgentId]);

  useEffect(() => {
    const mainEl = document.querySelector('main');
    if (mainEl) {
      mainEl.scrollTop = 0;
    }
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setSearchTerm(sessionIdParam || '');
    setDebouncedSearch(sessionIdParam || '');
    setConvoPage(1);
    setSelectedSession(null);
  }, [activeAiAgentId, sessionIdParam]);

  // Auto-select session if sessionIdParam matches any loaded session
  useEffect(() => {
    if (sessionIdParam && data?.sessions) {
      const match = data.sessions.find(s => s.session_id === sessionIdParam);
      if (match) {
        setSelectedSession(match);
      }
    }
  }, [data, sessionIdParam]);

  useEffect(() => {
    if (activeAiAgentId) {
      fetchData();
    }
  }, [activeAiAgentId, fetchData]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setConvoPage(1); // Reset to first page on search
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (selectedSession && !sessionTraces[selectedSession.session_id]) {
      fetchSessionTraces(selectedSession.session_id);
    }
  }, [selectedSession, sessionTraces, fetchSessionTraces]);

  const toggleTraceNode = (nodeId, e) => {
    if (e) e.stopPropagation();
    setExpandedTraceNodes(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  const fmtLatency = (ms) => {
    if (ms === null || ms === undefined) return '';
    if (ms < 1000) return `${Math.round(ms)}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const prettyJson = (val) => {
    if (!val) return 'null';
    if (typeof val === 'string') {
      try {
        return JSON.stringify(JSON.parse(val), null, 2);
      } catch {
        return val;
      }
    }
    return JSON.stringify(val, null, 2);
  };

  const isTraceMatch = (node, run) => {
    if (!node || !run) return false;

    // ⭐ ELITE: Prioritize ID-based matching (100% accurate)
    // We ensure we match the ROOT node of the execution (where parent_node_id is null or 'None')
    const isRoot = !node.parent_node_id || node.parent_node_id === 'None' || node.parent_node_id === '';
    if (run.run_id && node.run_id && run.run_id === node.run_id && isRoot) return true;

    // Fallback: Legacy fuzzy matching logic
    // Robust normalization: Strip markdown syntax (**), newlines (\n), punctuation, and extra whitespace
    const normalize = (str) => {
      if (!str) return '';
      let s = typeof str === 'string' ? str : JSON.stringify(str);
      return s.toLowerCase()
        .replace(/\\n/g, ' ')
        .replace(/\*\*/g, '')
        .replace(/[^a-z0-9\s]/g, '')
        .trim()
        .replace(/\s+/g, ' ');
    };
    const runIn = normalize(run.input_content);
    const runOut = normalize(run.assistant_content);
    const nodeIn = normalize(node.input_parsed);

    let nodeOut = normalize(node.output_parsed);

    if (isRoot) {
      if (runOut.length > 5 && (nodeOut.includes(runOut) || runOut.includes(nodeOut))) return true;
      if (runIn.length > 2 && (nodeIn.includes(runIn) || runIn.includes(nodeIn))) return true;
    }
    if (runIn.length > 2 && nodeIn.includes(runIn)) return true;
    return false;
  };

  if (isLoading && !data) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-5 sm:pb-6 border-b border-zinc-100 gap-3 sm:gap-4">
          <div>
            <div className="h-6 w-44 bg-zinc-200 rounded" />
            <div className="h-3 w-72 bg-zinc-100 rounded mt-2" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-1 bg-white rounded-xl border border-zinc-200 p-4 space-y-3 h-[600px]">
            <div className="h-9 w-full bg-zinc-100 rounded-lg" />
            <div className="space-y-2 pt-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="p-4 rounded-lg bg-zinc-50/70 border border-zinc-100 space-y-2 h-[100px] flex flex-col justify-center">
                  <div className="h-3.5 w-3/4 bg-zinc-200 rounded" />
                  <div className="h-2.5 w-1/2 bg-zinc-100 rounded" />
                </div>
              ))}
            </div>
          </div>
          <div className="md:col-span-2 bg-white rounded-xl border border-zinc-200 p-6 space-y-4 h-[600px] flex flex-col justify-between">
            <div className="space-y-3">
              <div className="h-5 w-48 bg-zinc-200 rounded" />
              <div className="h-3 w-64 bg-zinc-100 rounded" />
              <div className="pt-4 space-y-3">
                <div className="h-16 w-3/4 bg-zinc-50 rounded-xl border border-zinc-100" />
                <div className="h-16 w-2/3 bg-zinc-50 rounded-xl border border-zinc-100 ml-auto" />
                <div className="h-16 w-3/4 bg-zinc-50 rounded-xl border border-zinc-100" />
              </div>
            </div>
            <div className="h-12 w-full bg-zinc-100/60 rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  if ((data?.meta?.total_sessions === 0 || !data?.meta?.total_sessions) && !searchTerm) {
    return <EmptyAnalyticsState title="No conversations recorded" message="No conversations have been logged yet for this agent." />;
  }

  const sessions = data.agno_sessions || data.overview_stats?.agno_sessions || [];

  const SYSTEM_METADATA_KEYS = new Set([
    'agentid', 'aiagentid', 'agentkey', 'tenantid', 'teamid',
    'sessionid', 'sessionids', 'token', 'userid', 'user', 'email', 'name', 'phone',
    'username', 'filename', 'v', 'apiurl', 'apibase', 'channel', 'source',
    'mode', 'responsemode', 'streamingenabled', 'launchersize',
    'humanescalation', 'internetsearchenabled',
    'summaryenabled', 'summarymodelid', 'summaryinterval', 'learningenabled',
    'eyecatchertext', 'eyecatchertheme', 'eyecatcherenabled', 'eyecatcheranimation',
    'feedbackenabled', 'campaignsequentialdisplay',
    'osinfo', 'browserinfo', 'devicetype', 'ipaddress', 'ip', 'location',
    'latitude', 'longitude',
    'headercolor', 'userbubblecolor', 'botbubblecolor', 'sendbuttoncolor',
    'primarycolor', 'secondarycolor', 'backgroundcolor', 'textcolor',
    'fontfamily', 'fontsize', 'borderradius', 'position', 'size',
    'showclosebutton', 'showlauncher', 'customparameters', 'iconurl', 'brandlogourl',
    'brandlogo', 'icon', 'ispreview', 'welcomemessage', 'title'
  ]);

  const getFilteredMetadata = (session) => {
    if (!session) return [];
    return Object.entries({ ...(session.metadata || {}), ...(session.extra_data || {}) })
      .filter(([key]) => !SYSTEM_METADATA_KEYS.has(key.toLowerCase().replace(/[^a-z0-9]/g, '')));
  };

  const containerClass = "grid grid-cols-1 md:grid-cols-3 gap-6 animate-fadeIn";

  const sidebarClass = "md:col-span-1 bg-white rounded-xl border border-zinc-200 shadow-2xs flex flex-col h-[650px] overflow-hidden";

  const mainPanelClass = "md:col-span-2 bg-white rounded-xl border border-zinc-200 shadow-2xs flex flex-col overflow-hidden h-[650px]";

  return (
    <div className="space-y-6 animate-slideIn">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-5 sm:pb-6 border-b border-zinc-100 gap-3 sm:gap-4">
        <div>
          <h1 className="text-lg sm:text-xl font-bold text-zinc-900 tracking-tight">Conversation Hub</h1>
          <p className="text-xs text-zinc-500 mt-0.5 sm:mt-1">
            Deep-dive into individual sessions with full trace visibility.
          </p>
        </div>
      </div>

      <div className={containerClass}>
      <div className={sidebarClass}>
        {/* Search Bar */}
        <div className="p-4 border-b border-zinc-100 bg-white">
          <div className="relative group w-full">
            <input
              type="text"
              placeholder="Search by ID or User..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-zinc-50/50 border border-zinc-200 rounded-lg shadow-2xs focus:border-zinc-400 focus:ring-1 focus:ring-zinc-400 outline-none text-[13px] font-medium text-zinc-700 transition-all placeholder:text-zinc-400"
            />
            <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400">
              <Search className="w-4 h-4" />
            </div>
          </div>
        </div>

        <div className="overflow-y-auto custom-scrollbar flex-1" style={{ height: '480px', minHeight: '480px', maxHeight: '480px' }}>
          <div className="divide-y divide-zinc-100">
            {sessions.map(s => (
              <div
                key={s.session_id}
                onClick={() => setSelectedSession(s)}
                className={`p-5 cursor-pointer transition-all h-[110px] flex flex-col justify-center border-l-2 ${selectedSession?.session_id === s.session_id
                  ? 'bg-zinc-50 border-zinc-900 shadow-sm'
                  : 'bg-white border-transparent hover:bg-zinc-50/50'
                  }`}
              >
                <div className="flex items-center justify-between mb-3">
                  {(() => {
                    const channel = String(s.channel || '').toLowerCase();
                    const sid = String(s.session_id || '').toLowerCase();
                    let sourceName = 'Webchat';
                    let badgeClass = 'bg-zinc-100 text-zinc-600 border-zinc-200';
                    if (channel === 'widget' || sid.startsWith('chat_widget_')) {
                      sourceName = 'Chat Widget';
                    } else if (channel === 'whatsapp' || sid.startsWith('whatsapp_') || sid.startsWith('whatsapp:') || /^\d{7,}$/.test(sid)) {
                      sourceName = 'WhatsApp';
                    } else if (channel === 'telegram' || sid.startsWith('telegram_')) {
                      sourceName = 'Telegram';
                    } else if (channel === 'freshdesk' || sid.startsWith('freshdesk_')) {
                      sourceName = 'Freshdesk';
                    } else if (channel === 'freshchat' || sid.startsWith('freshchat_')) {
                      sourceName = 'Freshchat';
                    } else if (channel === 'slack' || sid.startsWith('slack_')) {
                      sourceName = 'Slack';
                    } else if (channel === 'zendesk' || sid.startsWith('zendesk_')) {
                      sourceName = 'Zendesk';
                    } else if (channel === 'email' || sid.startsWith('email_')) {
                      sourceName = 'Email';
                    } else if (channel === 'webhook' || sid.startsWith('webhook_')) {
                      sourceName = 'Webhook';
                    }

                    return (
                      <span className={`px-2 py-0.5 text-[8px] font-black uppercase tracking-wider font-semibold rounded border ${badgeClass}`}>
                        {sourceName}
                      </span>
                    );
                  })()}
                  <div className="flex items-center gap-1">
                    <Clock className="w-2.5 h-2.5 text-zinc-300" />
                    <span className="text-[9px] text-zinc-400 font-bold">
                      {new Date(s.created_at_iso).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3 mb-1.5">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center border overflow-hidden ${selectedSession?.session_id === s.session_id
                    ? 'border-zinc-900 shadow-md scale-110'
                    : 'border-zinc-200 shadow-sm'
                    }`}>
                    <img
                      src={`https://api.dicebear.com/7.x/micah/svg?seed=${getAvatarSeed(s.user_id, s.session_id)}&backgroundColor=b6e3f4,c0aede,d1d4f9`}
                      alt="User Avatar"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex flex-col min-w-0">
                    <div className={`text-[13px] font-bold truncate leading-tight ${selectedSession?.session_id === s.session_id ? 'text-zinc-900' : 'text-zinc-800'}`}>
                      {s.user_id || 'Anonymous'}
                    </div>
                    <div className={`text-[11px] font-medium mb-1 ${selectedSession?.session_id === s.session_id ? 'text-zinc-900' : 'text-zinc-500'}`}>
                      {s.runs_count} messages
                    </div>
                    <div className="text-[9px] font-mono font-bold text-zinc-300 uppercase tracking-tight truncate max-w-[200px]">
                      {s.session_id}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        {(data?.meta?.total_sessions || 0) > itemsPerPage && (
          <div className="p-4 border-t border-zinc-100 bg-zinc-50/50 flex items-center justify-between gap-4 mt-auto">
            <button
              onClick={() => setConvoPage(p => Math.max(1, p - 1))}
              disabled={convoPage === 1}
              className="px-3.5 py-1.5 bg-white border border-zinc-200 rounded-lg hover:bg-zinc-50 hover:border-zinc-300 transition-colors disabled:opacity-50 disabled:pointer-events-none text-[12px] font-semibold text-zinc-700 shadow-xs"
            >
              Prev
            </button>
            <div className="text-[12px] font-medium text-zinc-500">
              Page <span className="font-semibold text-zinc-800">{convoPage}</span> of <span className="font-semibold text-zinc-800">{Math.ceil((data?.meta?.total_sessions || 0) / itemsPerPage)}</span>
            </div>
            <button
              onClick={() => setConvoPage(p => p + 1)}
              disabled={convoPage >= Math.ceil((data?.meta?.total_sessions || 0) / itemsPerPage)}
              className="px-3.5 py-1.5 bg-white border border-zinc-200 rounded-lg hover:bg-zinc-50 hover:border-zinc-300 transition-colors disabled:opacity-50 disabled:pointer-events-none text-[12px] font-semibold text-zinc-700 shadow-xs"
            >
              Next
            </button>
          </div>
        )}
      </div>

      <div className={mainPanelClass}>
        {selectedSession ? (
          <div key={selectedSession.session_id} className="flex-1 flex flex-col min-w-0 bg-white overflow-hidden animate-fadeIn">
            <div className="px-8 py-5 border-b border-zinc-100 bg-white flex justify-between items-center sticky top-0 z-20 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center border border-zinc-100 overflow-hidden bg-white shadow-sm">
                  <img
                    src={`https://api.dicebear.com/7.x/micah/svg?seed=${getAvatarSeed(selectedSession.user_id, selectedSession.session_id)}&backgroundColor=b6e3f4,c0aede,d1d4f9`}
                    alt="User Avatar"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h3 className="font-bold text-xs uppercase tracking-wider font-semibold text-zinc-800 tracking-tight leading-none mb-1">
                    {selectedSession.user_id || 'Anonymous'}
                  </h3>
                  <div className="text-[10px] text-zinc-400 font-bold uppercase tracking-wider font-semibold">
                    Session History
                  </div>
                </div>
              </div>
              <div className="relative">
                <button
                  onClick={() => setShowParams(!showParams)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all ${showParams
                    ? 'bg-zinc-900 border-zinc-800 text-white shadow-xs'
                    : 'bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900'
                    }`}
                >
                  <Info size={14} />
                  <span className="text-[11px] font-black uppercase tracking-wider font-semibold">Details</span>
                </button>

                {showParams && (
                  <div className="absolute right-0 mt-2 w-72 bg-white border border-zinc-200 rounded-xl shadow-2xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-1">
                    <div className="p-3 border-b border-zinc-100 bg-zinc-50/50 flex justify-between items-center">
                      <span className="text-[10px] font-black text-zinc-400 uppercase tracking-wider font-semibold">Session ID</span>
                    </div>
                    <div className="p-3">
                      <div className="bg-zinc-50 p-2 rounded-lg border border-zinc-100 mb-3 break-all font-mono text-[10px] text-zinc-500 select-all">
                        {selectedSession.session_id}
                      </div>
                      {(() => {
                        const filteredMeta = getFilteredMetadata(selectedSession);
                        if (filteredMeta.length === 0) return null;
                        return (
                          <div className="space-y-3 mt-3">
                            <div className="text-[10px] font-black text-zinc-400 uppercase tracking-wider font-semibold">Metadata</div>
                            <div className="grid grid-cols-1 gap-2">
                              {filteredMeta.map(([key, value]) => (
                                <div key={key} className="flex justify-between items-center text-[11px] border-b border-zinc-50 pb-1">
                                  <span className="text-zinc-400 font-medium">{key}</span>
                                  <span className="text-zinc-700 font-bold">{String(value)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-10 space-y-12 bg-zinc-50/50 custom-scrollbar">
              {selectedSession.runs?.filter(run => run.agent_id !== 'private_note').map((run, i, arr) => {
                const traces = sessionTraces[selectedSession.session_id] || [];
                const rootNodes = traces.filter(n => !n.parent_node_id || n.parent_node_id === 'None' || n.parent_node_id === '');
                let rootNodeForRun = traces.find(node => isTraceMatch(node, run)) || rootNodes[i] || traces[i];
                const isFirstHumanMessage = run.agent_id === 'human_agent' && (i === 0 || arr[i - 1].agent_id !== 'human_agent');

                return (
                  <div key={i} className="space-y-6 relative animate-in fade-in slide-in-from-bottom-2 duration-300">
                    {isFirstHumanMessage && (
                      <div className="flex items-center my-8">
                        <div className="flex-1 border-t border-zinc-200"></div>
                        <span className="mx-4 px-4 py-1.5 text-xs font-medium text-zinc-500 bg-zinc-50/80 rounded-full border border-zinc-200 shadow-sm">
                          Connected to Human Agent
                        </span>
                        <div className="flex-1 border-t border-zinc-200"></div>
                      </div>
                    )}

                    {run.input_content && (
                      <div className="flex flex-row-reverse gap-3 items-center group">
                        <div className="w-7 h-7 rounded-full bg-zinc-100 flex items-center justify-center text-[10px] font-bold text-zinc-400 flex-shrink-0 mt-1">U</div>
                        <div
                          style={{ position: 'relative' }}
                          onMouseEnter={() => setHoveredRunId(`${i}_input`)}
                          onMouseLeave={() => setHoveredRunId(null)}
                          className="bg-white border border-zinc-200 px-4 py-2.5 rounded-xl rounded-tr-sm text-[13px] shadow-xs max-w-[80%] text-zinc-800 font-medium break-all whitespace-pre-wrap"
                        >
                          {renderMessageText(run.input_content)}
                          {hoveredRunId === `${i}_input` && (
                            <div style={{
                              position: 'absolute',
                              bottom: '100%',
                              left: '0',
                              marginBottom: '6px',
                              background: '#1e293b',
                              color: '#ffffff',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '10px',
                              whiteSpace: 'nowrap',
                              zIndex: 10,
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                              pointerEvents: 'none'
                            }}>
                              {formatTime(run.raw?.created_at)}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {run.assistant_content && (
                      <div className="flex gap-3 items-center relative group">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[12px] font-bold flex-shrink-0 mt-1 shadow-xs border ${run.agent_id === 'human_agent'
                            ? 'bg-zinc-900 text-white border-zinc-800'
                            : 'bg-white text-zinc-900 border-zinc-200'
                          }`}>
                          {run.agent_id === 'human_agent' ? 'H' : 'A'}
                        </div>
                        <div className="flex-1 max-w-[85%] relative flex items-center gap-3">
                          <div
                            onMouseEnter={() => setHoveredRunId(`${i}_assistant`)}
                            onMouseLeave={() => setHoveredRunId(null)}
                            onClick={() => {
                              const targetTraceId = rootNodeForRun?.node_id || `run_${i}`;

                              // 1. Toggle visibility of the trace container for this message
                              setConvoShownTraceIds(prev => {
                                const next = new Set();
                                if (!prev.has(targetTraceId)) next.add(targetTraceId);
                                return next;
                              });

                              // 2. Automatically expand the root node so the user doesn't have to click it separately
                              if (rootNodeForRun?.node_id) {
                                setExpandedTraceNodes(prev => {
                                  const next = new Set(prev);
                                  next.add(rootNodeForRun.node_id);
                                  return next;
                                });
                              }
                            }}
                            className={`px-5 py-3.5 rounded-xl rounded-tl-sm text-[13px] shadow-xs leading-relaxed font-medium transition-all duration-300 border border-zinc-200 break-all whitespace-pre-wrap relative ${run.agent_id === 'human_agent'
                                ? 'bg-zinc-900 text-white cursor-default'
                                : 'bg-zinc-50 text-zinc-900 cursor-pointer hover:bg-zinc-100 active:scale-[0.99]'
                              }`}
                          >
                            {renderMessageText(run.assistant_content)}
                            {hoveredRunId === `${i}_assistant` && (
                              <div style={{
                                position: 'absolute',
                                bottom: '100%',
                                right: '0',
                                marginBottom: '6px',
                                background: '#1e293b',
                                color: '#ffffff',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '10px',
                                whiteSpace: 'nowrap',
                                zIndex: 10,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                pointerEvents: 'none'
                              }}>
                                {formatTime(run.raw?.created_at)}
                              </div>
                            )}
                          </div>
                          {(convoShownTraceIds.has(rootNodeForRun?.node_id) || convoShownTraceIds.has(`run_${i}`)) && (
                            <div className="absolute -bottom-10 left-[-20px] w-10 h-10 border-l-2 border-b-2 border-zinc-100 rounded-bl-2xl" />
                          )}
                        </div>
                      </div>
                    )}

                    {(convoShownTraceIds.has(rootNodeForRun?.node_id) || convoShownTraceIds.has(`run_${i}`)) && (
                      <div className="pl-14 pr-4 pt-2 animate-in fade-in slide-in-from-top-4 duration-500">
                        {rootNodeForRun ? (
                          <TraceTreeNode
                            node={rootNodeForRun}
                            expandedNodes={expandedTraceNodes}
                            toggleNode={toggleTraceNode}
                            fmtLatency={fmtLatency}
                            prettyJson={prettyJson}
                          />
                        ) : (
                          <div className="p-4 bg-zinc-50 border border-zinc-200 rounded-xl text-xs font-mono text-zinc-500">
                            No execution traces recorded for this message run.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {selectedSession.handoff_status === 'resolved' && (
                <div className="flex items-center my-8">
                  <div className="flex-1 border-t border-zinc-200"></div>
                  <span className="mx-4 px-4 py-1.5 text-xs font-medium text-zinc-500 bg-zinc-50/80 rounded-full border border-zinc-200 shadow-sm">
                    Conversation Resolved
                  </span>
                  <div className="flex-1 border-t border-zinc-200"></div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-zinc-300">
            <Terminal className="w-16 h-16 opacity-10 mb-4" />
            <span className="font-bold text-xs uppercase tracking-wider font-semibold">Select target session</span>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
